import { memo } from 'react';

import cls from 'classnames';

import { Button, ButtonSizeXL, ButtonSkinTertiary } from '@picsart/design-system/components/Button';
import { Skeleton } from '@picsart/design-system/components/Skeleton';

import { TextSizes } from '@picsart/growth-rc/Title';
import { AllowedTextSizes } from '@picsart/growth-rc/types/lib/atoms/Description';
import { useTranslator } from '@picsart/rc/services/localization';

import usePageDataStore from 'stores/pageDataStore';
import MasonryGrid from 'components/TemplatesMasonryGrid';

import { useNavigate } from 'react-router-dom';
import CenteredContainer from '../CenteredContainer';
import TitleWithDescription from '../TitleWithDescription';
import useTemplatesData from './useTemplatesData';
import { ColorTemplatesProps } from './types';

import useStyles, { COLUMNS } from './styles';

function ColorTemplates({ copy: { title, description }, limit }: ColorTemplatesProps) {
  const navigate = useNavigate();
  const { category } = usePageDataStore(store => store.colorData);
  const colorName = usePageDataStore(store => store.pageData.data?.color);
  const classes = useStyles();
  const translate = useTranslator();
  const { data, isFetching } = useTemplatesData(category?.toLowerCase());
  const noResponseData = !data.length && !isFetching;

  if (noResponseData) return null;

  if (isFetching) {
    return (
      <CenteredContainer>
        <div className={classes.root} data-testid="loaders">
          <div>
            <Skeleton key="s1" className={classes.skeletonTitle} />
            <Skeleton key="s2" className={classes.skeletonDescription} />
          </div>
          <div className={classes.grid}>
            {Array(COLUMNS)
              .fill(0)
              .map((_, i) => (
                <Skeleton key={`t${i}`} className={classes.skeletonTemplate} />
              ))}
          </div>
        </div>
      </CenteredContainer>
    );
  }

  const handleButtonClick = () => {
    fetch(`https://api.picsart.com/content-category-items/template/${colorName}`)
      .then(response => {
        if (response.ok) {
          navigate(`/templates/${colorName}`);
        } else {
          navigate(`/templates/search?q=${colorName}`);
        }
      })
      .catch(() => {});
  };

  const masonaryLimitedData = data.slice(0, limit);
  const showLoadMoreButton = masonaryLimitedData.length === limit;

  return (
    <CenteredContainer>
      <div className={classes.root}>
        <TitleWithDescription
          title={{
            id: title?.id,
            tag: title?.tag,
            size: title?.size || TextSizes.Auto,
            text: translate(title?.text || 'Design templates with this color'),
          }}
          description={{
            id: description?.id,
            text: translate(
              description?.text ||
                'Save time with ready-to-use designs that are fully customizable.',
            ),
            size: description?.size || ('small' as AllowedTextSizes),
          }}
        />

        <MasonryGrid masonryData={masonaryLimitedData} />

        {showLoadMoreButton && (
          <div className={cls(classes.buttonContainer, 'flex-centered')}>
            <Button skin={ButtonSkinTertiary} size={ButtonSizeXL} onClick={handleButtonClick}>
              {translate('See more')}
            </Button>
          </div>
        )}
      </div>
    </CenteredContainer>
  );
}

export default memo(ColorTemplates);
